if (!customElements.get('product-info')) {
	// Utility function to safely escape CSS selectors
	const safeSelector = (selector) => {
		if (!selector) return '';
		// Escape CSS special characters
		return selector.replace(/[!"#$%&'()*+,.\/:;<=>?@[\]^`{|}~]/g, '\\$&');
	};

	customElements.define(
		'product-info',
		class ProductInfo extends HTMLElement {
			quantityInput = undefined;
			quantityForm = undefined;
			onVariantChangeUnsubscriber = undefined;
			onVariantChangeBundleUnsubscriber = undefined;
			onVariantChangeVariantBundleUnsubscriber = undefined;
			cartUpdateUnsubscriber = undefined;
			abortController = undefined;
			pendingRequestUrl = null;
			preProcessHtmlCallbacks = [];
			postProcessHtmlCallbacks = [];
			fetchingQueue = [];

			constructor() {
				super();
				this.quantityInput = this.querySelector('.quantity__input');
			}

			connectedCallback() {
				this.initializeProductSwapUtility();

				this.onVariantChangeUnsubscriber = subscribe(
					PUB_SUB_EVENTS.optionValueSelectionChange,
					this.handleOptionValueChange.bind(this),
				);

				this.onVariantChangeBundleUnsubscriber = subscribe(
					PUB_SUB_EVENTS.optionValueSelectionChangeBundle,
					this.handleOptionValueChangeBundle.bind(this),
				);

				this.initBundleTimestamp();
				this.initQuantityHandlers();
				this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
			}

			addPreProcessCallback(callback) {
				this.preProcessHtmlCallbacks.push(callback);
			}

			initBundleTimestamp() {
				if (!this.querySelector('.input-properties-bundle-timestamp')) return;
				this.cartUpdateUnsubscriber = subscribe(
					PUB_SUB_EVENTS.cartUpdate,
					this.updateInputTimestamp.bind(this),
				);
			}

			updateInputTimestamp() {
				const time = new Date().getTime();
				this.querySelectorAll(`.input-properties-bundle-timestamp`).forEach((input) => {
					input.value = time;
				});
			}

			initQuantityHandlers() {
				if (!this.quantityInput) return;

				this.quantityForm = this.querySelector('.product-form__quantity');
				if (!this.quantityForm) return;

				this.setQuantityBoundries();
				if (!this.dataset.originalSection) {
					this.cartUpdateUnsubscriber = subscribe(
						PUB_SUB_EVENTS.cartUpdate,
						this.fetchQuantityRules.bind(this),
					);
				}
			}

			disconnectedCallback() {
				this.onVariantChangeUnsubscriber();
				this.cartUpdateUnsubscriber?.();
			}

			initializeProductSwapUtility() {
				this.preProcessHtmlCallbacks.push((html) =>
					html
						.querySelectorAll('.scroll-trigger')
						.forEach((element) => element.classList.add('scroll-trigger--cancel')),
				);
				this.postProcessHtmlCallbacks.push((newNode) => {
					window?.Shopify?.PaymentButton?.init();
					window?.ProductModel?.loadShopifyXR();
				});
			}

			handleOptionValueChangeBundle({ data: { event, target, selectedOptionValues } }) {
				if (!this.contains(event.target)) return;
				const variantSelect = target.closest('variant-selects');
				const productUrl = variantSelect.dataset.productHandle;
				this.pendingRequestUrl = productUrl;
				const shouldFetchFullPage = false;
				const formId = variantSelect
					.querySelector('input[data-bundle-item-input]')
					?.getAttribute('form');

				this.renderProductInfo({
					requestUrl: this.buildRequestUrlWithParams(
						productUrl,
						selectedOptionValues,
						shouldFetchFullPage,
					),
					targetId: target.id,
					callback: this.handleUpdateProductInfoBundle(productUrl, variantSelect, formId),
				});
			}

			handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
				if (!this.contains(event.target)) return;

				this.resetProductFormState();

				// CORRECTION RADICALE: TOUJOURS utiliser l'URL du produit actuel pour les changements de variantes
				// Ne jamais changer de produit à moins que ce soit explicitement demandé
				let productUrl = this.dataset.url;

				// Reset du pendingRequestUrl pour éviter les problèmes de concurrence
				this.pendingRequestUrl = null;

				// Utiliser data-product-url seulement si c'est explicitement un changement de produit intentionnel
				const isIntentionalProductChange =
					target.dataset.productUrl &&
					target.dataset.productUrl !== this.dataset.url &&
					(target.closest('[data-allow-product-change]') ||
						target.hasAttribute('data-force-product-change'));

				if (isIntentionalProductChange) {
					productUrl = target.dataset.productUrl;
				}

				// Le pendingRequestUrl devrait être null pour les changements de variantes normaux
				this.pendingRequestUrl = isIntentionalProductChange ? productUrl : null;

				const shouldSwapProduct = this.dataset.url !== productUrl;
				const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

				this.renderProductInfo({
					requestUrl: this.buildRequestUrlWithParams(
						productUrl,
						selectedOptionValues,
						shouldFetchFullPage,
					),
					targetId: target.id,
					callback: shouldSwapProduct
						? this.handleSwapProduct(productUrl, shouldFetchFullPage)
						: this.handleUpdateProductInfo(productUrl, target),
				});
			}

			resetProductFormState() {
				const productForm = this.productForm;
				productForm?.toggleSubmitButton(true);
				productForm?.handleErrorMessage();
			}

			handleSwapProduct(productUrl, updateFullPage) {
				return (html) => {
					this.productModal?.remove();

					const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
					const variant = this.getSelectedVariant(html.querySelector(selector));

					this.updateURL(productUrl, variant?.id);

					if (updateFullPage) {
						document.querySelector('head title').innerHTML =
							html.querySelector('head title').innerHTML;

						HTMLUpdateUtility.viewTransition(
							document.querySelector('main'),
							html.querySelector('main'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks,
						);
					} else {
						HTMLUpdateUtility.viewTransition(
							this,
							html.querySelector('product-info'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks,
						);
					}
				};
			}

			renderProductInfo({ requestUrl, targetId, callback }) {
				// Annuler les requêtes en cours si la nouvelle requête est pour le même produit
				const currentProductUrl = this.dataset.url;
				const newProductUrl = requestUrl.split('?')[0];

				if (newProductUrl === currentProductUrl) {
					// Si c'est le même produit, annuler les anciennes requêtes pour éviter les conflits
					this.fetchingQueue = [];
				} else if (this.fetchingQueue.length > 2) {
					this.fetchingQueue = [];
				}

				this.fetchingQueue.push(requestUrl);

				this.querySelectorAll('.product-bundle__price').forEach((price) =>
					price.classList.add('loading'),
				);

				fetch(requestUrl)
					.then((response) => {
						return response.text();
					})
					.then((responseText) => {
						this.pendingRequestUrl = null;
						const html = new DOMParser().parseFromString(responseText, 'text/html');

						callback(html);
					})
					.then(() => {
						// set focus to last clicked option value with safe selector
						try {
							const element = document.querySelector(`#${safeSelector(targetId)}`);
							element?.focus();
						} catch (error) {
							// Silently handle invalid selectors
						}
					})
					.catch((error) => {
						if (error.name === 'AbortError') {
							console.log('Fetch aborted by user');
						} else {
							console.error(error);
						}
					})
					.finally(() => {
						this.fetchingQueue = this.fetchingQueue.filter((url) => url !== requestUrl);
						if (this.fetchingQueue.length === 0) {
							document.dispatchEvent(new CustomEvent('refreshPrice', { bubbles: true }));
							this.querySelectorAll('.product-bundle__price').forEach((price) =>
								price.classList.remove('loading'),
							);
						}
					});
			}

			getSelectedVariant(productInfoNode) {
				const selectedVariant = productInfoNode.querySelector(
					'variant-selects[data-main-picker] [data-selected-variant]',
				)?.innerHTML;
				return !!selectedVariant ? JSON.parse(selectedVariant) : null;
			}

			buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
				const params = [];

				if (this.sectionId) {
					!shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
				}

				if (optionValues.length) {
					params.push(`option_values=${optionValues.join(',')}`);
				}

				return `${url}?${params.join('&')}`;
			}

			updateOptionValues(html) {
				const variantSelects = html.querySelector('variant-selects');
				if (variantSelects) {
					HTMLUpdateUtility.viewTransition(
						this.variantSelectors,
						variantSelects,
						this.preProcessHtmlCallbacks,
					);
				}
			}

			updateOptionValuesBundle(html, productUrl, variant) {
				const variantSelects = html.querySelector('variant-selects[data-main-picker]');
				const currentVariantSelected = this.querySelector(
					`variant-selects[data-product-handle="${productUrl}"]`,
				);
				variantSelects.setAttribute('data-bundle', 'true');
				variantSelects.setAttribute('data-product-handle', productUrl);
				if (variant.useReducedPrice == 'true' || variant.compare_at_price === null) {
					variantSelects.setAttribute('data-variant-price', variant.price);
					variantSelects.setAttribute('data-variant-compare-price', 'null');
				} else {
					variantSelects.setAttribute('data-variant-price', variant.compare_at_price);
					variantSelects.setAttribute('data-variant-compare-price', variant.compare_at_price);
					variantSelects.setAttribute('data-variant-newprice', variant.price);
				}
				variantSelects.setAttribute('data-variant-price-club', variant.priceClub);
				if (variantSelects) {
					HTMLUpdateUtility.viewTransition(
						currentVariantSelected,
						variantSelects,
						this.preProcessHtmlCallbacks,
					);
				}
			}

			handleUpdateProductInfoBundle(productUrl, variantSelect, formId) {
				return (html) => {
					const variant = this.getSelectedVariant(html);
					this.updateOptionValuesBundle(html, productUrl, variant);
					this.updateVariantBundleInputs(html, variant, variantSelect, formId);
				};
			}

			handleUpdateProductInfo(productUrl, target) {
				return (html) => {
					// PROTECTION FINALE: Vérifier que nous ne changeons pas de produit par accident
					const newProductInfo = html.querySelector('product-info');
					const newUrl = newProductInfo?.dataset?.url;

					if (newUrl && newUrl !== this.dataset.url) {
						// Ne pas appliquer les changements si c'est un changement de produit non désiré
						return;
					}

					const variant = this.getSelectedVariant(html);

					const selectedValue = target.value;
					const productBundle = html.querySelector('product-bundle');
					const productUpsell = html.querySelector('product-upsells');
					let existingBundle = document.querySelector('product-bundle');
					let existingUpsell = document.querySelector('product-upsells');

					if (productBundle) {
						const matching = JSON.parse(this.dataset.matching);

						productBundle?.querySelectorAll('bundle-item')?.forEach((bundleItem) => {
							let matchingVariant = [];
							matching.matching.forEach((element) => {
								if (parseInt(element.nom_de_la_variante) === variant.id) {
									matchingVariant = element.noms_des_variantes_cibles;
								}
							});

							const selectedValue = bundleItem.querySelector(
								'select, fieldset input:checked',
							)?.value;

							bundleItem.querySelectorAll('select, fieldset input').forEach((selected) => {
								if (selected.tagName.toLowerCase() === 'select') {
									const options = selected.querySelectorAll('option');
									const matchingOptions = {};

									options.forEach((option) => {
										const variantId = option.dataset.variantId;
										if (variantId) {
											matchingOptions[variantId] = option;
										}
									});

									let foundMatch = false;

									if (matchingVariant && matchingVariant.length > 0) {
										for (const element of matchingVariant) {
											const matchOption = matchingOptions[element.variant_id];
											if (matchOption) {
												selected.value = matchOption.value;
												options.forEach((opt) => opt.removeAttribute('selected'));
												matchOption.setAttribute('selected', 'true');
												foundMatch = true;
												break;
											}
										}
									}

									if (!foundMatch) {
										if (selectedValue) {
											selected.value = selectedValue;
											const defaultOption = Array.from(options).find(
												(opt) => opt.value === selectedValue,
											);
											if (defaultOption) {
												options.forEach((opt) => opt.removeAttribute('selected'));
												defaultOption.setAttribute('selected', 'true');
											}
										} else {
											const firstOption = options[0];
											if (firstOption) {
												selected.value = firstOption.value;
												options.forEach((opt) => opt.removeAttribute('selected'));
												firstOption.setAttribute('selected', 'true');
											}
										}
									}

									selected.dataset.refresh = 'true';
								} else if (selected.tagName.toLowerCase() === 'input') {
									const variantId = selected.dataset.variantId;
									let matchingVariantItem = null;

									if (matchingVariant && matchingVariant.length > 0) {
										matchingVariantItem = matchingVariant.find(
											(match) => match.variant_id === variantId,
										);

										if (matchingVariantItem) {
											if (selected.type === 'checkbox' || selected.type === 'radio') {
												if (selected.checked) {
													selected.setAttribute('checked', 'true');
												}
											}
										}
									}
								}
							});
						});

						if (existingBundle) {
							existingBundle.replaceWith(productBundle);
						}

						existingBundle = document.querySelector('product-bundle');
						existingBundle.querySelectorAll('bundle-item').forEach((bundle) => {
							if (target.tagName.toLowerCase() === 'option') {
								bundle
									.querySelector('option[selected]')
									?.dispatchEvent(new Event('change', { bubbles: true }));
							} else if (target.tagName.toLowerCase() === 'input') {
								bundle
									.querySelector('input:checked')
									?.dispatchEvent(new Event('change', { bubbles: true }));
							}
						});
					}

					if (productUpsell) {
						const matching = JSON.parse(this.dataset.matching);
						productUpsell.querySelectorAll('variant-selects').forEach((variantSelect) => {
							let matchingVariant = [];
							matching?.matching.forEach((element) => {
								if (parseInt(element.nom_de_la_variante) === variant.id) {
									matchingVariant = element.noms_des_variantes_cibles;
								}
							});

							variantSelect.querySelectorAll('select, fieldset input').forEach((selected) => {
								if (selected.tagName.toLowerCase() === 'select') {
									const options = selected.querySelectorAll('option');
									const matchingOptions = {};

									// Créer un mapping des options par ID de variante
									options.forEach((option) => {
										const variantId = option.dataset.variantId;
										if (variantId) {
											matchingOptions[variantId] = option;
										}
									});

									// Rechercher une correspondance dans les variantes de mapping
									let foundMatch = false;

									if (matchingVariant && matchingVariant.length > 0) {
										// Parcourir les variantes correspondantes et trouver la première qui existe dans les options
										for (const element of matchingVariant) {
											const matchOption = matchingOptions[element.variant_id];
											if (matchOption) {
												selected.value = matchOption.value;
												options.forEach((opt) => opt.removeAttribute('selected'));
												matchOption.setAttribute('selected', 'true');
												foundMatch = true;
												break;
											}
										}
									}

									// Si aucune correspondance n'est trouvée, conserver la sélection actuelle
									if (!foundMatch) {
										if (selectedValue) {
											selected.value = selectedValue;
											const defaultOption = Array.from(options).find(
												(opt) => opt.value === selectedValue,
											);
											if (defaultOption) {
												options.forEach((opt) => opt.removeAttribute('selected'));
												defaultOption.setAttribute('selected', 'true');
											}
										}
									}

									// S'assurer qu'au moins une option est sélectionnée
									if (!selected.querySelector('option[selected="true"]')) {
										const firstOption = selected.querySelector('option');
										if (firstOption) {
											selected.value = firstOption.value;
											firstOption.setAttribute('selected', 'true');
										}
									}

									selected.dataset.refresh = 'true';
								} else if (selected.tagName.toLowerCase() === 'input') {
									const variantId = selected.dataset.variantId;
									const matchedVariant = matchingVariant?.find((v) => v.variant_id === variantId);
									if (matchedVariant) {
										if (selected.type === 'checkbox' || selected.type === 'radio') {
											selected.checked = true;
											selected.setAttribute('checked', 'true');
										}
									}
								}
							});

							// Mettre à jour les attributs de prix en fonction de la variante correspondante
							if (matchingVariant && matchingVariant.length > 0) {
								const selectedVariantId = variantSelect.querySelector(
									'[data-bundle-item-input]',
								).value;
								const matchedVariant = matchingVariant.find(
									(v) => v.variant_id === selectedVariantId,
								);
								if (matchedVariant && matchedVariant.price) {
									if (
										matchedVariant.compare_at_price &&
										matchedVariant.compare_at_price > matchedVariant.price
									) {
										variantSelect.dataset.variantPrice = matchedVariant.compare_at_price;
										variantSelect.dataset.variantNewprice = matchedVariant.price;
										variantSelect.dataset.variantComparePrice = matchedVariant.compare_at_price;
									} else {
										variantSelect.dataset.variantPrice = matchedVariant.price;
										variantSelect.dataset.variantNewprice = matchedVariant.price;
										variantSelect.dataset.variantComparePrice = 'null';
									}
								}
								if (matchedVariant && matchedVariant.price_club) {
									variantSelect.dataset.variantPriceClub = matchedVariant.price_club;
								}
							}
						});
						if (existingUpsell) {
							existingUpsell.replaceWith(productUpsell);
							// Actualiser les prix après la mise à jour des variantes
							document.querySelector('product-upsells')?.refreshPrice();
							// Émettre un événement personnalisé pour signaler que les variantes ont été mises à jour
							document.dispatchEvent(new CustomEvent('upsell:variants-updated', { bubbles: true }));
						}
					}

					this.pickupAvailability?.update(variant);
					this.updateOptionValues(html);
					this.updateURL(productUrl, variant?.id);
					this.updateVariantInputs(variant?.id);

					if (!variant) {
						this.setUnavailable();
						return;
					}

					this.updateMedia(html, variant?.featured_media?.id, variant?.featured_media?.alt);

					const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
						try {
							const sourceId = `${id}-${this.sectionUniqueId || this.sectionId}`;
							const destId = `${id}-${this.sectionUniqueId || this.dataset.section}`;

							// Pour getElementById, pas besoin d'échapper car on ne construit pas un sélecteur CSS
							// Mais pour querySelector, on doit échapper
							const source = html.getElementById(sourceId);
							const destination = this.querySelector(`#${safeSelector(destId)}`);

							if (source && destination) {
								destination.innerHTML = source.innerHTML;
								destination.classList.toggle('hidden', shouldHide(source));
							}
						} catch (error) {
							// Silently handle invalid selectors
						}
					};

					updateSourceFromDestination('price');
					updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
					updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
					updateSourceFromDestination('Volume');
					updateSourceFromDestination('Price-Per-Item', ({ classList }) =>
						classList.contains('hidden'),
					);
					updateSourceFromDestination('Dimensions');
					updateSourceFromDestination('Shipping-Estimate');
					updateSourceFromDestination('Alma-Badge');
					updateSourceFromDestination('product-club');
					updateSourceFromDestination('club-properties');

					this.updateQuantityRules(this.sectionId, html);
					try {
						this.querySelector(
							`#${safeSelector(`Quantity-Rules-${this.dataset.section}`)}`,
						)?.classList.remove('hidden');
						this.querySelector(
							`#${safeSelector(`Volume-Note-${this.dataset.section}`)}`,
						)?.classList.remove('hidden');
					} catch (error) {
						// Silently handle invalid selectors
					}

					this.productForm?.toggleSubmitButton(
						html
							.getElementById(`ProductSubmitButton-${this.sectionUniqueId || this.sectionId}`)
							?.hasAttribute('disabled') ?? true,
						window.variantStrings.soldOut,
					);

					publish(PUB_SUB_EVENTS.variantChange, {
						data: { sectionId: this.sectionId, html, variant },
					});
				};
			}

			updateVariantInputs(variantId) {
				try {
					const formSelector1 = safeSelector(
						`product-form-${this.sectionUniqueId || this.dataset.section}`,
					);
					const formSelector2 = safeSelector(
						`product-form-installment-${this.sectionUniqueId || this.dataset.section}`,
					);
					this.querySelectorAll(`#${formSelector1}, #${formSelector2}`).forEach((productForm) => {
						const input = productForm.querySelector('input[name="id"]');
						input.value = variantId ?? '';
						input.dispatchEvent(new Event('change', { bubbles: true }));
					});
				} catch (error) {
					// Silently handle invalid selectors
				}
			}

			updateVariantBundleInputs(html, variant, variantSelect, formId) {
				const currentVariantSelected = this.querySelector(
					`variant-selects[data-product-handle="${variantSelect.dataset.productHandle}"]`,
				);

				if (!currentVariantSelected) return;

				const inputVariant = variantSelect.querySelector('input[data-bundle-item-input]');
				if (!inputVariant) return;

				currentVariantSelected
					.querySelectorAll('input[data-bundle-item-input]')
					.forEach((input) => {
						if (input.getAttribute('form') === formId) input.remove();
					});

				currentVariantSelected.querySelectorAll('input[data-price-input]').forEach((input) => {
					if (input.getAttribute('form') === formId) input.remove();
				});

				const cloneInput = inputVariant.cloneNode(true);
				cloneInput.value = variant.id;
				cloneInput.setAttribute('form', formId);
				currentVariantSelected.insertAdjacentHTML('beforeend', cloneInput.outerHTML);

				const inputPrice = variantSelect.querySelector('input[data-price-input]');
				if (inputPrice) {
					const clonePrice = inputPrice.cloneNode(true);
					clonePrice.setAttribute('form', formId);
					clonePrice.dataset.priceInput = variant.useReducedPrice;
					if (variantSelect.dataset.priceInput === 'true' || variant.compare_at_price === null) {
						clonePrice.value = variant.price / 100;
					} else {
						clonePrice.value = variant.compare_at_price / 100;
					}
					currentVariantSelected.insertAdjacentHTML('beforeend', clonePrice.outerHTML);
				}
			}

			updateURL(url, variantId) {
				const newUrl = `${url}${variantId ? `?variant=${variantId}` : ''}`;

				this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${newUrl}`);

				if (this.dataset.updateUrl === 'false') return;
				window.history.replaceState({}, '', newUrl);
			}

			setUnavailable() {
				this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

				try {
					const selectors = [
						'price',
						'Inventory',
						'Sku',
						'Price-Per-Item',
						'Volume-Note',
						'Volume',
						'Quantity-Rules',
					]
						.map((id) => `#${safeSelector(`${id}-${this.dataset.section}`)}`)
						.join(', ');
					document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
				} catch (error) {
					// Silently handle invalid selectors
				}
			}

			updateMedia(html, variantFeaturedMediaId, alt) {
				if (!variantFeaturedMediaId) return;

				const mediaGallerySource = this.querySelector('media-gallery ul');
				const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

				if (!mediaGallerySource || !mediaGalleryDestination) return;

				const refreshSourceData = () => {
					if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
					const mediaGallerySourceItems = Array.from(
						mediaGallerySource.querySelectorAll('li[data-media-id]'),
					);
					const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
					const sourceMap = new Map(
						mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]),
					);
					return [mediaGallerySourceItems, sourceSet, sourceMap];
				};

				if (mediaGallerySource && mediaGalleryDestination) {
					let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
					const mediaGalleryDestinationItems = Array.from(
						mediaGalleryDestination.querySelectorAll('li[data-media-id]'),
					);
					const destinationSet = new Set(
						mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId),
					);
					let shouldRefresh = false;

					// add items from new data not present in DOM
					for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
						if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
							mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
							shouldRefresh = true;
						}
					}

					// remove items from DOM not present in new data
					for (let i = 0; i < mediaGallerySourceItems.length; i++) {
						if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
							mediaGallerySourceItems[i].remove();
							shouldRefresh = true;
						}
					}

					// refresh
					if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

					// if media galleries don't match, sort to match new data order
					mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
						const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

						if (sourceData && sourceData.index !== destinationIndex) {
							mediaGallerySource.insertBefore(
								sourceData.item,
								mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`),
							);

							// refresh source now that it has been modified
							[mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
						}
					});
				}

				// set featured media as active in the media gallery
				try {
					const mediaId = safeSelector(`${this.dataset.section}-${variantFeaturedMediaId}`);
					this.querySelector(`media-gallery`)?.setActiveMedia?.(mediaId, true);
				} catch (error) {
					// Silently handle invalid selectors
				}

				// update product medias
				const updatedMedia = () => {
					const mediaGallerySourceItems = Array.from(
						mediaGallerySource.querySelectorAll('li[data-alt]'),
					);

					mediaGallerySourceItems.forEach((item) => {
						if (item.dataset.alt === alt || item.dataset.alt.indexOf(alt) !== -1 || alt === null) {
							item.classList.remove('hidden');
						} else {
							item.classList.add('hidden');
						}
					});
				};

				updatedMedia();

				// update media modal
				const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
				const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
				if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
			}

			setQuantityBoundries() {
				const data = {
					cartQuantity: this.quantityInput.dataset.cartQuantity
						? parseInt(this.quantityInput.dataset.cartQuantity)
						: 0,
					min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
					max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
					step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
				};

				let min = data.min;
				const max = data.max === null ? data.max : data.max - data.cartQuantity;
				if (max !== null) min = Math.min(min, max);
				if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

				this.quantityInput.min = min;

				if (max) {
					this.quantityInput.max = max;
				} else {
					this.quantityInput.removeAttribute('max');
				}
				this.quantityInput.value = min;

				publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
			}

			fetchQuantityRules() {
				const currentVariantId = this.productForm?.variantIdInput?.value;
				if (!currentVariantId) return;

				this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
				fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
					.then((response) => response.text())
					.then((responseText) => {
						const html = new DOMParser().parseFromString(responseText, 'text/html');
						this.updateQuantityRules(this.dataset.section, html);
					})
					.catch((e) => console.error(e))
					.finally(() =>
						this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'),
					);
			}

			updateQuantityRules(sectionId, html) {
				if (!this.quantityInput) return;
				this.setQuantityBoundries();

				const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
				const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
				for (let selector of selectors) {
					const current = this.quantityForm.querySelector(selector);
					const updated = quantityFormUpdated.querySelector(selector);
					if (!current || !updated) continue;
					if (selector === '.quantity__input') {
						const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
						for (let attribute of attributes) {
							const valueUpdated = updated.getAttribute(attribute);
							if (valueUpdated !== null) {
								current.setAttribute(attribute, valueUpdated);
							} else {
								current.removeAttribute(attribute);
							}
						}
					} else {
						current.innerHTML = updated.innerHTML;
					}
				}
			}

			get productForm() {
				return this.querySelector(`product-form`);
			}

			get productModal() {
				try {
					const modalId = safeSelector(`ProductModal-${this.dataset.section}`);
					return document.querySelector(`#${modalId}`);
				} catch (error) {
					// Silently handle invalid selectors
					return null;
				}
			}

			get pickupAvailability() {
				return this.querySelector(`pickup-availability`);
			}

			get variantSelectors() {
				return this.querySelector('variant-selects');
			}

			get relatedProducts() {
				const relatedProductsSectionId = SectionId.getIdForSection(
					SectionId.parseId(this.sectionId),
					'related-products',
				);
				return document.querySelector(
					`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
				);
			}

			get quickOrderList() {
				const quickOrderListSectionId = SectionId.getIdForSection(
					SectionId.parseId(this.sectionId),
					'quick_order_list',
				);
				return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
			}

			get sectionId() {
				return this.dataset.originalSection || this.dataset.section;
			}

			get sectionUniqueId() {
				return this.dataset.sectionUniqueId;
			}
		},
	);
}
